import { useAtomValue } from 'jotai'
import { useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { toast } from '@app/components/ui/Toast/toast'

import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu'

import { cn } from '@/lib/tailwind.utils'
import { ERROR_403_MESSAGE } from '@app/app-constants'
import { useMainTabs } from '@app/components/Guide/hooks/useMainTab'
import { GuideNavbarSkeleton } from '@app/components/Guide/ui/GuideNavbarSkeleton'
import { appLink } from '@app/config/appLink'
import { clientNameAtom } from '@app/state/clientsAtom'
import { generateLogoUrls } from '@app/utils/generateLogoUrls'
import { GuideSearch } from '@app/modules/GuideV2/components/Guide.search'
import NavTriggerV2 from '../../HomeV2/components/SidebarToggleTrigger'

const GuideNavbar = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { data: guideNavData, isLoading, error } = useMainTabs()
  const clientName = useAtomValue(clientNameAtom)
  const logo = useMemo(() => generateLogoUrls(clientName), [clientName])

  useEffect(() => {
    if (error?.message === ERROR_403_MESSAGE) {
      toast({
        type: 'error',
        description:
          'You do not have permission to view this content. Please contact your administrator.',
        position: 'top-right',
      })
      navigate(appLink.homepage.path)
    }
  }, [error, navigate])

  if (isLoading) return <GuideNavbarSkeleton />
  if (error) return <GuideNavbarSkeleton isError />

  return (
    <header
      className={cn(
        'sticky top-0 z-50 flex h-14 w-full shrink-0 items-center gap-2 border-b bg-background transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-14',
      )}
    >
      <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
        <NavTriggerV2 />

        <div className="flex flex-1 justify-center">
          <NavigationMenu>
            <NavigationMenuList>
              {guideNavData?.map((item) => {
                const firstSub = item.sub_tabs?.[0]?.slug
                const href = firstSub
                  ? `/app/guide/${item.slug}/${firstSub}`
                  : `/app/guide/${item.slug}`

                const isActive = pathname.startsWith(`/app/guide/${item.slug}`)

                return (
                  <NavigationMenuItem key={item.id}>
                    <NavigationMenuLink
                      data-active={isActive || undefined}
                      className={cn(
                        navigationMenuTriggerStyle(),
                        'cursor-pointer',
                        isActive && 'bg-accent text-accent-foreground',
                      )}
                      onClick={() => navigate(href)}
                    >
                      {item.title}
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                )
              })}
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        <div className="flex min-w-[120px] items-center justify-end gap-3">
          <GuideSearch />

          <img src={logo.full} alt="Logo" className="h-8 w-auto" />
        </div>
      </div>
    </header>
  )
}

export default GuideNavbar
